import { Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { InlineAlert, Loader, LoadingButton } from "src/grading/components";
import { PageHeader } from "src/grading/layout/PageHeader";
import { EVENT_LABEL, EVENT_UNIT, EventId } from "src/models/sessions";
import { useGetStudentVideoQuery } from "src/store/reducers/documents/documentsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  useGetReviewQuery,
  usePutEntryMutation,
} from "src/store/reducers/sessions/sessionsApiSlice";

const fmt = (eventId: string, value: number) =>
  eventId === "pft-mile"
    ? `${Math.floor(value / 60)}:${String(Math.round(value % 60)).padStart(2, "0")}`
    : String(value);
const parse = (eventId: string, text: string) => {
  if (eventId === "pft-mile" && text.includes(":")) {
    const [m, s] = text.split(":").map(Number);
    return m * 60 + s;
  }
  return Number(text);
};

/**
 * Teacher video review for a self-test or camera-assisted entry: play the recording, count reps
 * or time with the graders, then confirm or adjust the recorded value (audited as a teacher edit).
 */
const VideoReviewPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sessionId, entryId } = useParams<{
    sessionId: string;
    entryId: string;
  }>();
  const { data, isLoading } = useGetReviewQuery(sessionId);
  const [putEntry, { isLoading: saving }] = usePutEntryMutation();
  const entry = useMemo(
    () => data?.session.entries.find((e) => e.id === entryId),
    [data, entryId],
  );
  const student = useMemo(
    () => data?.students.find((s) => s.studentId === entry?.studentId),
    [data, entry],
  );
  const { data: video, isError: noVideo } = useGetStudentVideoQuery(
    {
      studentId: entry?.studentId ?? "",
      testResultId: entry?.testResultId ?? "",
      exerciseId: entry?.eventId ?? "",
    },
    { skip: !entry?.testResultId },
  );
  const [count, setCount] = useState(0);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const unit = entry ? EVENT_UNIT[entry.eventId as EventId] : "reps";

  const sessionName = data?.session.name;
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({ label: t("tabs.sessions"), path: "/grading/sessions" }),
    );
    dispatch(
      addBreadcrumb({
        label: sessionName ?? t("console.session"),
        path: `/grading/sessions/${sessionId}`,
      }),
    );
    dispatch(
      addBreadcrumb({
        label: t("review.title"),
        path: `/grading/sessions/${sessionId}/review`,
      }),
    );
    dispatch(
      addBreadcrumb({ label: t("review.video", { defaultValue: "Video" }) }),
    );
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, sessionId, sessionName, t]);
  useEffect(() => {
    if (entry) setValue(fmt(entry.eventId, Number(entry.value)));
  }, [entry]);

  if (isLoading || !data) return <Loader />;
  if (!entry || !student)
    return (
      <InlineAlert severity="error">
        {t("video.entryNotFound", { defaultValue: "Entry not found." })}
      </InlineAlert>
    );
  const locked = data.session.status === "finalized";

  const confirm = async (newValue: number) => {
    setError(null);
    setSaved(false);
    try {
      await putEntry({
        sessionId,
        entry: {
          id: entry.id,
          studentId: entry.studentId,
          eventId: entry.eventId as EventId,
          attempt: entry.attempt ?? 1,
          value: newValue,
          unit,
          mode: entry.mode,
          modification: entry.modification,
          modificationNote: entry.modificationNote ?? undefined,
          advisoryCount: entry.advisoryCount ?? undefined,
          recordedAt: new Date().toISOString(),
        },
      }).unwrap();
      setSaved(true);
    } catch (e) {
      setError(
        (e as { data?: { message?: string } })?.data?.message ??
          t("video.saveError", { defaultValue: "Could not save the count." }),
      );
    }
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
      <PageHeader
        title={t("video.title", {
          defaultValue: "Video review · {{name}}",
          name: `${student.firstName} ${student.lastName}`,
        })}
        backTo={`/grading/sessions/${sessionId}/review`}
        backLabel={t("review.title")}
        description={
          <>
            {t(`events.${entry.eventId}`, {
              defaultValue:
                EVENT_LABEL[entry.eventId as EventId] ?? entry.eventId,
            })}{" "}
            ·{" "}
            {t("video.recorded", {
              defaultValue: "recorded {{value}}",
              value: `${fmt(entry.eventId, Number(entry.value))} ${unit}`,
            })}
            {entry.advisoryCount !== null && entry.advisoryCount !== undefined
              ? ` · ${t("video.camera", {
                  defaultValue: "camera {{value}}",
                  value: fmt(entry.eventId, Number(entry.advisoryCount)),
                })}`
              : ""}
            {entry.recordedByType === "self-test"
              ? ` · ${t("video.selfTest", { defaultValue: "self-test" })}`
              : ""}
          </>
        }
      />

      <Paper sx={{ p: 2, mb: 3, borderRadius: `${radius.card}px` }}>
        {video ? (
          <Box
            component="video"
            controls
            src={video.url}
            aria-label={t("video.recording", {
              defaultValue: "Exercise recording",
            })}
            sx={{
              display: "block",
              width: "100%",
              maxHeight: 480,
              borderRadius: `${radius.control}px`,
              bgcolor: (theme) => theme.palette.tokens.strong,
            }}
          >
            <track kind="captions" />
          </Box>
        ) : noVideo || !entry.testResultId ? (
          <InlineAlert severity="info">
            {t("video.noRecording", {
              defaultValue:
                "No recording is stored for this entry. Camera-assisted station entries keep only the advisory count; videos are deleted after the district's retention window.",
            })}
          </InlineAlert>
        ) : (
          <Loader />
        )}
      </Paper>

      <Paper
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRadius: `${radius.card}px`,
        }}
        component="section"
        aria-labelledby="graders"
      >
        <Typography id="graders" variant="h6" component="h2">
          {t("video.countTitle", { defaultValue: "Count while you watch" })}
        </Typography>
        {unit === "reps" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h3" component="output" aria-live="polite">
              {count}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{ borderRadius: `${radius.pill}px` }}
              onClick={() => setCount((c) => c + 1)}
              disabled={locked}
            >
              {t("video.plusOne", { defaultValue: "+1 rep" })}
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: `${radius.pill}px` }}
              onClick={() => setCount((c) => Math.max(0, c - 1))}
              disabled={locked}
            >
              {t("common.undo")}
            </Button>
            <Button
              sx={{ borderRadius: `${radius.pill}px` }}
              onClick={() => setCount(0)}
              disabled={locked}
            >
              {t("video.reset", { defaultValue: "Reset" })}
            </Button>
            <LoadingButton
              variant="contained"
              color="success"
              sx={{ borderRadius: `${radius.pill}px` }}
              loading={saving}
              disabled={locked}
              onClick={() => confirm(count)}
              label={t("video.useCount", {
                defaultValue: "Use {{count}}",
                count,
              })}
            />
          </Box>
        ) : (
          <Typography sx={{ color: "text.secondary" }}>
            {unit === "seconds"
              ? t("video.timerSeconds", {
                  defaultValue:
                    "Use the video timer to check the time held or finish time, then confirm or adjust below.",
                })
              : t("video.timerLaps", {
                  defaultValue:
                    "Use the video timer to check the laps, then confirm or adjust below.",
                })}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            label={
              entry.eventId === "pft-mile"
                ? t("video.timeLabel", { defaultValue: "Time (m:ss)" })
                : t("video.valueLabel", {
                    defaultValue: "Value ({{unit}})",
                    unit,
                  })
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={locked}
            size="small"
          />
          <LoadingButton
            variant="contained"
            sx={{ borderRadius: `${radius.pill}px` }}
            loading={saving}
            disabled={locked || !value}
            onClick={() => confirm(parse(entry.eventId, value))}
            label={t("video.confirmValue", { defaultValue: "Confirm value" })}
          />
          {entry.advisoryCount !== null &&
            entry.advisoryCount !== undefined && (
              <Chip
                label={t("video.cameraSaid", {
                  defaultValue: "Camera said {{value}}",
                  value: fmt(entry.eventId, Number(entry.advisoryCount)),
                })}
              />
            )}
        </Box>
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
        {saved && (
          <InlineAlert severity="success">
            {t("video.saved", {
              defaultValue:
                "Saved as a teacher edit; the audit trail records the previous value.",
            })}
          </InlineAlert>
        )}
        {locked && (
          <InlineAlert severity="info">
            {t("video.locked", {
              defaultValue:
                "The session is finalized. Reopen it to change this entry.",
            })}
          </InlineAlert>
        )}
      </Paper>
    </Box>
  );
};

export default VideoReviewPage;
